import { useReducer, useEffect, useRef, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  readQualifyData,
  saveQualifyData,
  clearQualifyData,
  buildMessageBlock,
  type QualifyData,
} from '@/lib/producer-qualify';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { track } from '@/lib/analytics';
import { wizardReducer, INITIAL_STATE } from './reducer';
import { canGoBack, canGoNext, isLastStep, ctaLabel, validationHint, stepIndex, totalSteps } from './selectors';
import { STEP_IDS, type StepId } from './types';
import type { QualifySelectedPlace } from '@/lib/producer-qualify';

/* ── Session step key (separate from draft data) ──── */
const STEP_KEY = 'farmly_qualify_step';

function readPersistedStep(): StepId | null {
  try {
    const v = sessionStorage.getItem(STEP_KEY);
    if (v && (STEP_IDS as readonly string[]).includes(v)) return v as StepId;
  } catch {}
  return null;
}

function persistStep(step: StepId): void {
  try { sessionStorage.setItem(STEP_KEY, step); } catch {}
}

/* ── Hook ────────────────────────────────────────────── */

export function useQualifyWizard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [state, dispatch] = useReducer(wizardReducer, INITIAL_STATE);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const hydratedRef = useRef(false);

  // Logged-in user email (stable source of truth)
  const authEmail = user?.email ?? '';

  /* ── Hydrate once from sessionStorage ─────────────── */
  useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;

    const stored = readQualifyData();
    const persistedStep = readPersistedStep();

    dispatch({
      type: 'HYDRATE',
      draft: stored
        ? {
            selectedPlace: stored.selected_place,
            producerName: stored.producer_name,
            offerings: stored.offerings,
            contactEmail: stored.contact_email || authEmail,
          }
        : { contactEmail: authEmail },
      step: persistedStep ?? undefined,
    });

    try { track('producer_qualify_started', { source_surface: '/for-producers' }); } catch {}
  }, [authEmail]);

  /* ── Track step views ─────────────────────────────── */
  const prevStepRef = useRef<StepId | null>(null);
  useEffect(() => {
    if (!state.hydrated) return;
    if (prevStepRef.current === state.currentStep) return;
    prevStepRef.current = state.currentStep;

    // Persist step position
    persistStep(state.currentStep);

    try {
      track('producer_qualify_step_viewed', {
        step_id: state.currentStep,
        step_index: String(stepIndex(state.currentStep) + 1),
      });
    } catch {}
  }, [state.hydrated, state.currentStep]);

  /* ── Debounced persist of draft data ──────────────── */
  const persistTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    if (!state.hydrated) return;
    clearTimeout(persistTimer.current);
    persistTimer.current = setTimeout(() => {
      const payload: QualifyData = {
        selected_place: state.draft.selectedPlace,
        producer_name: state.draft.producerName.trim(),
        offerings: state.draft.offerings,
        contact_email: state.draft.contactEmail.trim(),
        created_at_iso: new Date().toISOString(),
      };
      saveQualifyData(payload);
    }, 400);
    return () => clearTimeout(persistTimer.current);
  }, [state.draft.selectedPlace, state.draft.producerName, state.draft.offerings, state.draft.contactEmail, state.hydrated]);

  /* ── Completion — call existing producer-interest edge fn ── */
  const handleComplete = useCallback(async () => {
    setSubmitting(true);
    setSubmitError(null);

    const data: QualifyData = {
      selected_place: state.draft.selectedPlace,
      producer_name: state.draft.producerName.trim(),
      offerings: state.draft.offerings,
      contact_email: state.draft.contactEmail.trim(),
      created_at_iso: new Date().toISOString(),
    };
    saveQualifyData(data);

    // Build area text from place
    const areaText = state.draft.selectedPlace
      ? [state.draft.selectedPlace.municipality, state.draft.selectedPlace.landskap].filter(Boolean).join(', ')
      : '';

    try {
      const res = await supabase.functions.invoke('producer-interest', {
        body: {
          business_name: state.draft.producerName.trim(),
          area_text: areaText,
          email: state.draft.contactEmail.trim(),
          message: buildMessageBlock(data),
        },
      });

      if (res.error) {
        const body = res.data as { error?: string; message_sv?: string } | null;
        if (body?.error === 'rate_limited') {
          setSubmitError(body.message_sv ?? 'Försök igen om en stund.');
        } else if (body?.error === 'validation') {
          setSubmitError('Kontrollera att namn och e-post är ifyllda.');
        } else {
          setSubmitError('Något gick fel. Försök igen.');
        }
        try { track('producer_interest_submit_failed', { error_kind: body?.error ?? 'unknown' }); } catch {}
        setSubmitting(false);
        return;
      }

      // Success
      try {
        track('producer_qualify_completed', {
          has_place: String(!!state.draft.selectedPlace),
          producer_name_length: String(state.draft.producerName.trim().length),
          offerings_count: String(state.draft.offerings.length),
          source_surface: '/for-producers',
        });
        track('producer_interest_submit_succeeded', {} as Record<string, never>);
      } catch {}

      clearQualifyData();
      try { sessionStorage.removeItem(STEP_KEY); } catch {}
      navigate('/for-producers/qualify/success');
    } catch {
      setSubmitError('Något gick fel. Försök igen.');
      try { track('producer_interest_submit_failed', { error_kind: 'network' }); } catch {}
    } finally {
      setSubmitting(false);
    }
  }, [state.draft, navigate]);

  /* ── Navigation ───────────────────────────────────── */
  const goNext = useCallback(() => {
    if (submitting) return;
    if (!canGoNext(state)) return;
    if (isLastStep(state)) {
      handleComplete();
      return;
    }
    dispatch({ type: 'GO_NEXT' });
  }, [state, handleComplete, submitting]);

  const goBack = useCallback(() => {
    if (!canGoBack(state)) {
      navigate('/for-producers');
      return;
    }
    dispatch({ type: 'GO_BACK' });
  }, [state, navigate]);

  const goToStep = useCallback((step: StepId) => {
    dispatch({ type: 'GO_TO_STEP', step });
  }, []);

  /* ── Data setters ─────────────────────────────────── */
  const setSelectedPlace = useCallback((place: QualifySelectedPlace | null) => {
    dispatch({ type: 'SET_SELECTED_PLACE', place });
  }, []);

  const setProducerName = useCallback((name: string) => {
    dispatch({ type: 'SET_PRODUCER_NAME', name });
  }, []);

  const setOfferings = useCallback((offerings: string[]) => {
    dispatch({ type: 'SET_OFFERINGS', offerings });
  }, []);

  const setContactEmail = useCallback((email: string) => {
    dispatch({ type: 'SET_CONTACT_EMAIL', email });
  }, []);

  /* ── Public API ───────────────────────────────────── */
  return {
    currentStep: state.currentStep,
    currentStepIndex: stepIndex(state.currentStep),
    totalSteps: totalSteps(),
    hydrated: state.hydrated,

    selectedPlace: state.draft.selectedPlace,
    producerName: state.draft.producerName,
    offerings: state.draft.offerings,
    contactEmail: state.draft.contactEmail,
    isLoggedIn: !!user,

    submitting,
    submitError,

    setSelectedPlace,
    setProducerName,
    setOfferings,
    setContactEmail,

    goNext,
    goBack,
    goToStep,
    canGoNext: canGoNext(state) && !submitting,
    canGoBack: canGoBack(state),
    isLastStep: isLastStep(state),
    ctaLabel: submitting ? 'Skickar...' : ctaLabel(state),
    validationHint: validationHint(state),
  } as const;
}
